import { useCallback, useState } from 'react';

import DAIToken from '../ContractABI/DAIToken.json';
import USDCToken from '../ContractABI/USDCToken.json';
import OZKToken from '../ContractABI/ozUSD.json';
import {
  REACT_APP_DAI_TOKEN,
  REACT_APP_ETH_RPC,
  REACT_APP_OPENZK_RPC,
  REACT_APP_OZK_TOKEN,
  REACT_APP_OZUSD_L2_TOKEN,
  REACT_APP_OZUSD_TOKEN,
  REACT_APP_USDC_TOKEN,
  REACT_APP_USDC_TOKEN_DECIMALS,
  REACT_APP_USDT_TOKEN,
  REACT_APP_USDT_TOKEN_DECIMALS,
} from '../Config/Config';
import { useAccount } from 'wagmi';
import { AppConfig } from '../Config/app-config';
import BigNumber from 'bignumber.js';
import Web3 from 'web3';

export function useBalance() {
  const { isConnected } = useAccount();

  const [usdtBalance, setUsdtBalance] = useState('0');
  const [usdcBalance, setUsdcBalance] = useState('0');
  const [daiBalance, setDaiBalance] = useState('0');
  const [ethBalance, setEthBalance] = useState('0');
  const [ozEthBalance, setOzEthBalance] = useState('0');
  const [ozEthL1Balance, setOzEthL1Balance] = useState('0');
  const [ozUsdBalance, setOzUsdBalance] = useState('0');
  const [ozUsdL1Balance, setOzUsdL1Balance] = useState('0');

  const loadL1Balance = useCallback(
    async (address: string) => {
      if (!isConnected) return;
      const web3 = new Web3(REACT_APP_ETH_RPC);

      const usdcContract = new web3.eth.Contract(
        USDCToken,
        REACT_APP_USDC_TOKEN,
      );

      const usdtContract = new web3.eth.Contract(
        USDCToken,
        REACT_APP_USDT_TOKEN,
      );

      const daiContract = new web3.eth.Contract(DAIToken, REACT_APP_DAI_TOKEN);

      const [usdtResult, usdcResult, daiResult] = await Promise.all([
        (await usdtContract.methods.balanceOf(address).call()) as string,
        (await usdcContract.methods.balanceOf(address).call()) as string,
        (await daiContract.methods.balanceOf(address).call()) as string,
      ]);

      if (AppConfig.isDev) {
        const ethContract = new web3.eth.Contract(
          DAIToken,
          REACT_APP_OZK_TOKEN,
        );
        const ethBalance = (await ethContract.methods
          .balanceOf(address)
          .call()) as string;
        setEthBalance(
          BigNumber(ethBalance.toString()).div(Math.pow(10, 18)).toString(),
        );
      } else {
        const ethBalance = await web3.eth.getBalance(address);
        setEthBalance(
          BigNumber(ethBalance.toString()).div(Math.pow(10, 18)).toString(),
        );
      }

      setUsdtBalance(
        BigNumber(usdtResult.toString())
          .div(Math.pow(10, REACT_APP_USDT_TOKEN_DECIMALS))
          .toString(),
      );
      setUsdcBalance(
        BigNumber(usdcResult.toString())
          .div(Math.pow(10, REACT_APP_USDC_TOKEN_DECIMALS))
          .toString(),
      );
      setDaiBalance(
        BigNumber(daiResult.toString()).div(Math.pow(10, 18)).toString(),
      );
    },
    [isConnected],
  );

  const loadL2Balance = useCallback(
    async (address: string) => {
      if (!isConnected) return;

      const web3 = new Web3(REACT_APP_OPENZK_RPC);

      const ozkContract = new web3.eth.Contract(
        OZKToken,
        REACT_APP_OZUSD_L2_TOKEN,
      );
      const [ozEthResult, ozUsdResult] = await Promise.all([
        web3.eth.getBalance(address),
        (await ozkContract.methods.balanceOf(address).call()) as string,
      ]);
      setOzUsdBalance(BigNumber(ozUsdResult).div(Math.pow(10, 18)).toString());
      setOzEthBalance(
        BigNumber(ozEthResult.toString()).div(Math.pow(10, 18)).toString(),
      );
    },
    [isConnected],
  );

  const loadWithdrawBalance = useCallback(
    async (address: string) => {
      if (!address || !isConnected) return;

      const web3 = new Web3(REACT_APP_ETH_RPC);

      const ozUsdContract = new web3.eth.Contract(
        DAIToken,
        REACT_APP_OZUSD_TOKEN,
      );
      const ethContract = new web3.eth.Contract(DAIToken, REACT_APP_OZK_TOKEN);

      const [ozUsdResult, ozEthL1Result] = await Promise.all([
        (await ozUsdContract.methods.balanceOf(address).call()) as string,
        (await ethContract.methods.balanceOf(address).call()) as string,
      ]);

      setOzEthL1Balance(
        BigNumber(ozEthL1Result.toString()).div(Math.pow(10, 18)).toString(),
      );
      setOzUsdL1Balance(
        BigNumber(ozUsdResult.toString()).div(Math.pow(10, 18)).toString(),
      );
    },
    [isConnected],
  );

  const resetL1Balance = useCallback(() => {
    setUsdtBalance('0');
    setUsdcBalance('0');
    setDaiBalance('0');
  }, []);

  const resetL2Balance = useCallback(() => {
    setOzEthBalance('0');
    setOzUsdBalance('0');
  }, []);

  const resetWithdrawBalance = useCallback(() => {
    setOzEthL1Balance('0');
    setOzUsdL1Balance('0');
  }, []);

  return {
    loadL1Balance,
    loadL2Balance,
    resetL1Balance,
    resetL2Balance,
    ethBalance,
    ozEthBalance,
    ozUsdBalance,
    usdcBalance,
    usdtBalance,
    daiBalance,
    loadWithdrawBalance,
    resetWithdrawBalance,
    ozUsdL1Balance,
    ozEthL1Balance,
  };
}
